import { Headline } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React from 'react';
import styled from 'styled-components';
import { SYS_BORDER_RADIUS_MD } from '@oneaudi/unified-web-common';
import { useContent } from '../context/ContentContext';
import { useTrackingManager } from '../hooks/useTrackingManager';
import Actions from './Actions';
import { Badges } from './Badges';
import { CardImage } from './CardImage';
import ConsumptionAndEmission from './ConsumptionAndEmission';
import { Price } from './Price';
import type { Carline } from '../utils/filterUtils';
import { getConfigureUrl } from '../utils/getConfigureLink';
import type { CarlineConsumptionAndEmissionSummary } from '../../graphql/documents/carline-structure.generated';

export interface CardProps {
  readonly index: number;
  readonly consumptionAndEmission?: CarlineConsumptionAndEmissionSummary;
  readonly carline: Carline;
}

export const Card: FC<CardProps> = ({ index, carline }) => {
  const {
    identifier: { id: carlineId },
    name: carlineName,
    prices: { minPrice },
    availableStock,
    fuelTypes,
    group,
    markAsNew,
    isFake,
    consumptionAndEmission,
    exteriorViews,
    links,
  } = carline;
  const configureLink = getConfigureUrl(carline);
  const price = !isFake ? minPrice?.value : undefined;
  const {
    caeDisplayOption: { showCae },
  } = useContent();

  const { clickCardImage } = useTrackingManager();

  const headlineFontSize = '4';
  const headlineFontWeight = process.env.IS_LEGACY ? 'normal' : 'bold';

  return (
    <CardWrapper data-carlineid={carlineId} data-testid={`carCard-${carlineId}`}>
      <ImageWrapper
        backDropNumber="1"
        onClick={() =>
          clickCardImage(group, carlineName, links?.carlineStartPage?.url ?? undefined)
        }
      >
        <Badges fuelTypes={fuelTypes} markAsNew={markAsNew} className="BadgeCarlineCard" />
        <CardImage
          loading={index === 0 ? 'eager' : 'lazy'}
          alt={carlineName}
          imageUrl={exteriorViews?.side}
          href={links?.carlineStartPage?.url || undefined}
        />
      </ImageWrapper>
      <ContentWrapper>
        <HeadingSection>
          <Title
            variant={{
              type: 'headline',
              order: headlineFontSize,
              weight: headlineFontWeight,
            }}
            className="carlineTitle"
            tag="h4"
          >
            {carlineName}
          </Title>
          <Price value={price} />
        </HeadingSection>
        <Actions
          title={carlineName}
          discoverLink={links?.carlineStartPage?.url || undefined}
          configureLink={configureLink || undefined}
          availableStock={availableStock || undefined}
          group={group}
          carline={{
            id: carlineId,
            name: carlineName,
            group,
          }}
        />
        {consumptionAndEmission?.summary && showCae && (
          <DisclaimerWrapper>
            <ConsumptionAndEmission
              consumptionData={consumptionAndEmission.summary}
              carlineName={carlineName}
            />
          </DisclaimerWrapper>
        )}
      </ContentWrapper>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  overflow: hidden;
  height: calc(100% - var(--spacing-relative-lg));
  background-color: ${({ theme }) => theme.colors.backgroundCard};
  border-radius: ${() => (process.env.IS_LEGACY ? '0' : SYS_BORDER_RADIUS_MD)};
  display: flex;
  flex-direction: column;

  &:last-child {
    padding-bottom: var(--spacing-relative-lg);
  }
`;

const ImageWrapper = styled.div<{ backDropNumber: string }>`
  position: relative;
  font-size: 0;
  overflow: hidden;
  padding-top: ${({ theme }) => theme.spacing.fixed300};
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.fixed200};
  flex: 1;
  padding: var(--spacing-relative-lg) var(--spacing-relative-lg) 0;
`;

const Title = styled(Headline)`
  text-wrap: pretty;
`;

const DisclaimerWrapper = styled.div`
  position: relative;
`;

const HeadingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.fixed50};
`;
